/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog, IconButton, InlineNotification, Pagination, Search, Typography2 } from '@coursera/cds-core';
import { AddIcon, TrashIcon } from '@coursera/cds-icons';

import { learnerTableStyles } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/learnerTableStyles';
import type { Learner } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import useLearnerList, { LEARNER_LIST_PAGE_SIZE } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import { MOCK_ORG_LEARNERS } from 'bundles/enterprise-admin-messaging-v2/utils/mockLearners';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  /** The cohort the editor opens with — the user's working copy starts here. */
  initialLearners: Learner[];
  /** Persist the working copy back to the orchestrator and navigate to Review. */
  onSave: (next: Learner[]) => void;
  /**
   * Restore the AI cohort and navigate to the read-only Learner list. Also
   * clears the manual-edit flag in the orchestrator.
   */
  onResetToOriginal: () => void;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);

    /* CDS Search's outer wrapper + inner input root both need an explicit
       width: 100% to fill the parent — the inputRoot carries
       min-width: 240px but no width override of its own. */
    .cds-Search-base,
    .cds-Search-inputRoot {
      width: 100%;
    }
  `,
  actionCell: css`
    width: 72px;
    text-align: right;
  `,
};

/**
 * Editable cohort: trash an existing learner to remove them; type into the
 * search box to surface learners outside the cohort with an "+ Add" pill.
 * Working copy lives locally; Save changes commits back to the orchestrator.
 */
const EditLearnerListScreen = ({ initialLearners, onSave, onResetToOriginal }: Props) => {
  const [editedList, setEditedList] = React.useState<Learner[]>(initialLearners);
  const [bannerDismissed, setBannerDismissed] = React.useState(false);

  // "All learners" minus the current cohort. Any overlap between the org
  // pool and the cohort is treated as part of the cohort — the same
  // learner should never appear with both a trash icon and a +Add button.
  const availablePool = React.useMemo(() => {
    const cohortIds = new Set(editedList.map((l) => l.id));
    return MOCK_ORG_LEARNERS.filter((l) => !cohortIds.has(l.id));
  }, [editedList]);

  const { searchQuery, setSearchQuery, page, setPage, pageCount, pageRows, filteredCount } = useLearnerList({
    list: editedList,
    availablePool,
  });

  const handleAdd = React.useCallback((learner: Learner) => {
    setEditedList((previous) => (previous.some((l) => l.id === learner.id) ? previous : [...previous, learner]));
  }, []);

  const handleRemove = React.useCallback((learnerId: string) => {
    setEditedList((previous) => previous.filter((l) => l.id !== learnerId));
  }, []);

  const handleSave = React.useCallback(() => {
    onSave(editedList);
  }, [editedList, onSave]);

  const handleReset = React.useCallback(() => {
    // No local state reset needed — onResetToOriginal restores the original
    // cohort in the orchestrator and navigates away, unmounting this screen.
    onResetToOriginal();
  }, [onResetToOriginal]);

  return (
    <React.Fragment>
      <Dialog.Content>
        <div css={styles.body}>
          {!bannerDismissed && (
            <InlineNotification
              severity="information"
              onDismiss={() => setBannerDismissed(true)}
              data-testid="ai-message-modal-edit-learner-banner"
            >
              {_t('Editing learner list will remove auto updates in learner cohort based on AI')}
            </InlineNotification>
          )}

          <Search
            aria-label={_t('Search and add learners')}
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={_t('Search and add learners')}
            hideSearchButton
            data-testid="ai-message-modal-edit-learner-search"
          />

          <div css={learnerTableStyles.tableWrapper}>
            <table
              css={learnerTableStyles.table}
              aria-label={_t('Add or remove learners')}
              data-testid="ai-message-modal-edit-learner-table"
            >
              <thead>
                <tr>
                  <th css={[learnerTableStyles.th, learnerTableStyles.nameColumn]}>
                    <Typography2 component="span" variant="subtitleMedium" color="supportText">
                      {_t('Name')}
                    </Typography2>
                  </th>
                  <th css={learnerTableStyles.th}>
                    <Typography2 component="span" variant="subtitleMedium" color="supportText">
                      {_t('Email')}
                    </Typography2>
                  </th>
                  <th css={[learnerTableStyles.th, styles.actionCell]} aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td css={learnerTableStyles.emptyRow} colSpan={3}>
                      <Typography2 component="span" variant="bodySecondary">
                        {_t('No learners match your search.')}
                      </Typography2>
                    </td>
                  </tr>
                ) : (
                  pageRows.map(({ learner, inList }) => (
                    <tr key={learner.id} data-testid={`ai-message-modal-edit-learner-row-${learner.id}`}>
                      <td css={learnerTableStyles.td}>
                        <Typography2 component="span" variant="bodyPrimary">
                          {learner.name}
                        </Typography2>
                      </td>
                      <td css={learnerTableStyles.td}>
                        <Typography2 component="span" variant="bodyPrimary">
                          {learner.email}
                        </Typography2>
                      </td>
                      <td css={[learnerTableStyles.td, styles.actionCell]}>
                        {inList ? (
                          <IconButton
                            variant="ghost"
                            size="small"
                            icon={<TrashIcon />}
                            aria-label={_t('Remove #{name}', { name: learner.name })}
                            tooltip={_t('Remove from list')}
                            onClick={() => handleRemove(learner.id)}
                            data-testid={`ai-message-modal-edit-learner-remove-${learner.id}`}
                          />
                        ) : (
                          <Button
                            variant="secondary"
                            size="small"
                            icon={<AddIcon />}
                            iconPosition="before"
                            onClick={() => handleAdd(learner)}
                            data-testid={`ai-message-modal-edit-learner-add-${learner.id}`}
                          >
                            {_t('Add')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {pageCount > 1 && (
              <div css={learnerTableStyles.paginationRow}>
                <Pagination
                  total={filteredCount}
                  pageSize={LEARNER_LIST_PAGE_SIZE}
                  page={page}
                  onPageChange={(_event, nextPage) => setPage(nextPage)}
                  hidePageSizeInput
                  aria-label={_t('Add or remove learners pagination')}
                />
              </div>
            )}
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div css={learnerTableStyles.actions}>
          <Button variant="primary" onClick={handleSave} data-testid="ai-message-modal-edit-learner-save">
            {_t('Save changes')}
          </Button>
          <Button variant="secondary" onClick={handleReset} data-testid="ai-message-modal-edit-learner-reset">
            {_t('Go back to original list')}
          </Button>
        </div>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default EditLearnerListScreen;
